import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2, Clock, ShieldCheck, FileText,
  AlertCircle, Lock, Users, ClipboardCheck,
} from 'lucide-react';
import { ReadinessRow } from '@/components/ui/readiness-row.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { customInstance } from '@edison/api-client-react';
import type { AcknowledgementRequest } from './claim-detail.js';

const CRITERION_LABELS: Record<string, string> = {
  systematic_progression: 'Systematic Progression',
  technical_uncertainty: 'Technical Uncertainty',
  new_knowledge: 'New Knowledge',
  contemporaneous_documentation: 'Contemporaneous Documentation',
};

const CODE_ICONS: Record<string, React.ReactNode> = {
  TFA_LOCKED: <ShieldCheck className="h-4 w-4" />,
  TFA_NOT_LOCKED: <ShieldCheck className="h-4 w-4" />,
  DETERMINATION_OK: <ShieldCheck className="h-4 w-4" />,
  CERTAIN_DETERMINATION: <ShieldCheck className="h-4 w-4" />,
  IP_OWNERSHIP_OK: <Users className="h-4 w-4" />,
  CLIENT_IP_OWNERSHIP: <Users className="h-4 w-4" />,
  UNDETERMINED_IP_OWNERSHIP: <Users className="h-4 w-4" />,
  COMPETENCE_CONFIRMED: <CheckCircle2 className="h-4 w-4" />,
  NO_CONFIRMED_COMPETENCE: <CheckCircle2 className="h-4 w-4" />,
  APPROVED_ENTRIES: <Clock className="h-4 w-4" />,
  NO_APPROVED_ENTRIES: <Clock className="h-4 w-4" />,
  ALL_ENTRIES_REVIEWED: <Clock className="h-4 w-4" />,
  UNREVIEWED_ENTRIES: <Clock className="h-4 w-4" />,
  CONTRIBUTORS_VERIFIED: <Users className="h-4 w-4" />,
  UNVERIFIED_CONTRIBUTORS: <Users className="h-4 w-4" />,
  RATE_CARDS_OK: <FileText className="h-4 w-4" />,
  MISSING_RATE_CARDS: <FileText className="h-4 w-4" />,
};

interface ReadinessCheck {
  code: string;
  status: 'ok' | 'warning' | 'blocking';
  message: string;
  detail?: string;
  state: 'done' | 'progress' | 'blocked' | 'locked';
}

interface ReadinessData {
  checks: ReadinessCheck[];
  blockingCount: number;
  readyToValidate: boolean;
  readyToLock: boolean;
  claimStatus: string;
}

interface Props {
  claimId: string;
  claimStatus: string;
  onValidate: () => void;
  onLock: () => void;
  isValidating: boolean;
  isLocking: boolean;
  validationFailures: Array<{ code: string; message: string }>;
  pendingAcks?: AcknowledgementRequest[] | null;
  ackTexts?: Record<string, string>;
  onAckChange?: (key: string, text: string) => void;
}

export function ClaimReadinessTab({
  claimId,
  claimStatus,
  onValidate,
  onLock,
  isValidating,
  isLocking,
  validationFailures,
  pendingAcks,
  ackTexts = {},
  onAckChange,
}: Props) {
  const { data, isLoading } = useQuery<ReadinessData>({
    queryKey: ['claim-readiness', claimId],
    queryFn: async () => {
      const res = await customInstance<{ data: ReadinessData }>({
        url: `/api/claims/${claimId}/readiness`,
        method: 'GET',
      });
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 rounded-xl bg-surface-3 animate-pulse" />
        ))}
      </div>
    );
  }

  // Determine if the lock button should be enabled when acks are pending
  const acksComplete =
    !pendingAcks?.length ||
    pendingAcks.every((ack) => (ackTexts[`${ack.projectId}:${ack.criterion}`] ?? '').length >= 20);

  return (
    <div className="space-y-6">
      {/* Validation / lock failures */}
      {validationFailures.length > 0 && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 space-y-2">
          <div className="flex items-center gap-2 text-destructive text-sm font-medium">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Resolve these issues before proceeding:
          </div>
          <ul className="space-y-1 pl-6 list-disc text-sm text-destructive">
            {validationFailures.map((f) => (
              <li key={f.code}>{f.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Readiness checklist */}
      <div className="space-y-1">
        {(data?.checks ?? []).map((check) => (
          <ReadinessRow
            key={check.code}
            state={check.state}
            icon={CODE_ICONS[check.code] ?? <CheckCircle2 className="h-4 w-4" />}
            name={check.message}
            detail={check.detail}
          />
        ))}
      </div>

      {/* Defensibility acknowledgements form — appears after first lock attempt if acks are needed */}
      {pendingAcks && pendingAcks.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-4">
          <div className="flex items-start gap-2">
            <ClipboardCheck className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Written acknowledgements required</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                For each criterion below, describe what off-system evidence covers the gap (minimum 20 characters).
              </p>
            </div>
          </div>
          <div className="space-y-4">
            {pendingAcks.map((ack) => {
              const key = `${ack.projectId}:${ack.criterion}`;
              const text = ackTexts[key] ?? '';
              return (
                <div key={key} className="space-y-1.5">
                  <label htmlFor={`ack-${key}`} className="flex items-center gap-2 text-xs font-medium">
                    <span>{ack.projectName}</span>
                    <span className="text-muted-foreground">—</span>
                    <span>{CRITERION_LABELS[ack.criterion] ?? ack.criterion}</span>
                    <Badge variant="warning">
                      {ack.score.charAt(0).toUpperCase() + ack.score.slice(1)}
                    </Badge>
                  </label>
                  <textarea
                    id={`ack-${key}`}
                    rows={3}
                    value={text}
                    onChange={(e) => onAckChange?.(key, e.target.value)}
                    placeholder="Describe the off-system evidence that covers this criterion (e.g. internal technical reports, dated design documents, email threads)…"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                  />
                  {text.length > 0 && text.length < 20 && (
                    <p className="text-xs text-destructive">{20 - text.length} more characters required</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Status footer + actions */}
      <div className="border-t border-border/40 pt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {claimStatus === 'locked'
            ? 'Claim is locked. Outputs available in the Schedule 1 tab.'
            : pendingAcks?.length
            ? 'Provide written acknowledgements for each Weak/Gap criterion, then lock.'
            : claimStatus === 'validating'
            ? 'Validation passed — lock to generate ATO outputs.'
            : (data?.blockingCount ?? 0) > 0
            ? `${data!.blockingCount} item${data!.blockingCount > 1 ? 's' : ''} need${data!.blockingCount === 1 ? 's' : ''} attention before validating.`
            : 'All checks passed — ready to validate.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {claimStatus === 'draft' && data?.readyToValidate && (
            <Button
              variant="secondary"
              onClick={onValidate}
              disabled={isValidating}
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {isValidating ? 'Validating…' : 'Run validation'}
            </Button>
          )}
          {claimStatus === 'draft' && !data?.readyToValidate && (
            <Button
              variant="secondary"
              onClick={onValidate}
              disabled={isValidating || (data?.blockingCount ?? 0) > 0}
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {isValidating ? 'Validating…' : 'Run validation'}
            </Button>
          )}
          {(claimStatus === 'validating' || pendingAcks?.length) && (
            <Button
              variant="primary"
              onClick={onLock}
              disabled={isLocking || !acksComplete}
            >
              <Lock className="h-4 w-4" aria-hidden="true" />
              {isLocking ? 'Locking…' : pendingAcks?.length ? 'Confirm lock' : 'Lock claim'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
